import { useEffect } from 'react';
import { usePageTitle } from '@/hooks/use-page-title';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { useHeaderAction } from '@/lib/header-action-context';
import { Link } from 'react-router';
import { Card } from '@/components/ui/card';
import { AppWindow, ArrowRight, Layers, GitCommit, Cpu, LayoutDashboard } from 'lucide-react';
import type { Application } from '@/types';

export default function DashboardPage() {
    usePageTitle('Dashboard');
    const { setHeaderTitle } = useHeaderAction();

    useEffect(() => {
        setHeaderTitle({
            icon: (
                <div className="flex h-7 w-7 items-center justify-center rounded-md bg-indigo-100 text-indigo-600">
                    <LayoutDashboard size={16} />
                </div>
            ),
            title: 'Dashboard',
            subtitle: "Overview of your platform's AI capabilities and resources.",
        });
        return () => setHeaderTitle(null);
    }, [setHeaderTitle]);
    const { data, isLoading } = useQuery({
        queryKey: ['dashboard'],
        queryFn: () => api.get('/dashboard').then((r) => r.data),
    });

    if (isLoading) return <div className="p-6">Loading...</div>;

    const metrics = data?.metrics;
    const recentApps: Application[] = data?.recent_applications || [];

    const summaryCards = [
        {
            label: 'Capabilities',
            value: metrics?.capabilities ?? 0,
            icon: Layers,
            color: 'text-indigo-600',
            bg: 'bg-indigo-100',
            borderAccent: 'border-l-indigo-500',
        },
        {
            label: 'Applications',
            value: metrics?.applications ?? 0,
            icon: AppWindow,
            color: 'text-cyan-600',
            bg: 'bg-cyan-100',
            borderAccent: 'border-l-cyan-500',
        },
        {
            label: 'Revisions',
            value: metrics?.revisions ?? 0,
            icon: GitCommit,
            color: 'text-emerald-600',
            bg: 'bg-emerald-100',
            borderAccent: 'border-l-emerald-500',
        },
        {
            label: 'Methods',
            value: metrics?.methods ?? 0,
            icon: Cpu,
            color: 'text-amber-600',
            bg: 'bg-amber-100',
            borderAccent: 'border-l-amber-500',
        },
    ];

    return (
        <div className="p-6 space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {summaryCards.map((card) => {
                    const Icon = card.icon;
                    return (
                        <Card
                            key={card.label}
                            className={`p-4 border-l-4 ${card.borderAccent}`}
                        >
                            <div className="flex items-center gap-3">
                                <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${card.bg}`}>
                                    <Icon size={20} className={card.color} />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">{card.label}</p>
                                    <p className="text-2xl font-bold text-slate-900">{card.value}</p>
                                </div>
                            </div>
                        </Card>
                    );
                })}
            </div>

            {recentApps.length > 0 && (
                <div>
                    <h2 className="font-semibold text-slate-900 mb-4">Recent Applications</h2>
                    <div
                        className="grid justify-start"
                        style={{
                            gridTemplateColumns: 'repeat(auto-fill, minmax(265px, 322px))',
                            gap: 'clamp(10px, 2vw, 20px)',
                        }}
                    >
                        {recentApps.map((app) => (
                            <Link
                                key={app.id}
                                to={`/applications/${app.app_key}`}
                                className="block"
                            >
                                <div className="relative flex min-h-32 w-full flex-col rounded-lg border border-slate-200 border-l-4 border-l-cyan-500 bg-white p-3 shadow-sm transition-all duration-200 hover:cursor-pointer hover:border-slate-300 hover:bg-slate-50 hover:shadow-md hover:shadow-blue-100/50">
                                    <div className="absolute right-2 top-2 z-10">
                                        <ArrowRight size={14} className="text-slate-400" />
                                    </div>
                                    <div className="flex flex-row items-start gap-2">
                                        <div className="flex shrink-0 items-center justify-center rounded-md p-1.5 bg-cyan-100 text-cyan-600">
                                            <AppWindow size={18} />
                                        </div>
                                        <div className="flex flex-1 flex-col">
                                            <span className="text-left text-sm font-bold text-slate-800 leading-tight">
                                                {app.name || app.app_key}
                                            </span>
                                            <span className="text-left text-[10px] text-slate-500 font-mono">
                                                {app.app_key}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
